import React, { useState, useRef, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useProject, useProjectDispatch } from '../../store/projectContext';
import { PROJECT_ACTIONS } from '../../store/projectReducer';
import { useCurrentFrame } from '../use-current-frame';
import { useVideoConfig } from '../use-video-config';
import { useTimelinePosition } from '../timeline-position-state';
import { SequenceContext } from '../SequenceContext';
import { getActiveTextOverlays } from './textOverlayUtils';
import { getEnterStyle, getExitStyle } from './textOverlayPresets';
import FadeAnimation from '../TextAnimation/FadeAnimation';
import RiseAnimation from '../TextAnimation/RiseAnimation';
import PopAnimation from '../TextAnimation/PopAnimation';
import PanAnimation from '../TextAnimation/PanAnimation';
import WipeAnimation from '../TextAnimation/WipeAnimation';
import BreatheAnimation from '../TextAnimation/BreatheAnimation';
import SlideUpAnimation from '../TextAnimation/SlideUpAnimation';
import TypeWritingAnimation from '../TextAnimation/TypeWritingAnimation';
import TitleCardAnimation from '../TextAnimation/TitleCardAnimation';

const COMPONENT_ANIMATIONS = {
  comp_fade: FadeAnimation,
  comp_rise: RiseAnimation,
  comp_pop: PopAnimation,
  comp_pan: PanAnimation,
  comp_wipe: WipeAnimation,
  comp_breathe: BreatheAnimation,
  comp_slideUp: SlideUpAnimation,
  comp_typewriter: TypeWritingAnimation,
  comp_titleCard: TitleCardAnimation,
};

function isComponentAnimation(animId) {
  return animId && animId.startsWith('comp_');
}

/**
 * Provides a SequenceContext that offsets the frame so useCurrentFrame()
 * returns 0 at the overlay's startTime. This makes TextAnimation components
 * animate from the beginning when the overlay appears.
 */
const AnimFrameProvider = ({ overlay, fps, children }) => {
  const startFrame = Math.round(overlay.startTime * fps);
  const durationInFrames = Math.round((overlay.endTime - overlay.startTime) * fps);
  const contextValue = useMemo(() => ({
    cumulatedFrom: startFrame,
    relativeFrom: 0,
    durationInFrames,
    parentFrom: 0,
    id: overlay.id,
  }), [startFrame, durationInFrames, overlay.id]);

  return (
    <SequenceContext.Provider value={contextValue}>
      {children}
    </SequenceContext.Provider>
  );
};

const OverlayContainer = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 15;
  container-type: inline-size;
`;

const TextItem = styled.div`
  position: absolute;
  pointer-events: auto;
  cursor: grab;
  user-select: none;
  white-space: pre-wrap;
  word-wrap: break-word;
  border: 2px solid transparent;
  transition: border-color 0.15s;
  &:hover { border-color: rgba(99,102,241,0.5); }
  &:active { cursor: grabbing; }
`;

const EditableText = styled.div`
  outline: none;
  white-space: pre-wrap;
  min-height: 1.2em;
  min-width: 40px;
`;

const TextOverlayRenderer = () => {
  const project = useProject();
  const dispatch = useProjectDispatch();
  const frame = useCurrentFrame();
  const fps = project?.fps || 30;
  const timeSec = frame / fps;
  const overlays = getActiveTextOverlays(project?.textOverlays, timeSec);
  const [editingId, setEditingId] = useState(null);
  const dragRef = useRef(null);
 
  const handleDragStart = useCallback((e, overlay) => {
    if (editingId === overlay.id) return;
    e.preventDefault();
    const container = e.currentTarget.parentElement;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const origX = overlay.x;
    const origY = overlay.y;

    const onMove = (ev) => {
      const dx = (ev.clientX - startX) / rect.width;
      const dy = (ev.clientY - startY) / rect.height;
      const newX = Math.max(0, Math.min(1, origX + dx));
      const newY = Math.max(0, Math.min(1, origY + dy));
      dispatch({
        type: PROJECT_ACTIONS.UPDATE_TEXT_OVERLAY,
        payload: { id: overlay.id, updates: { x: newX, y: newY } },
      });
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [dispatch, editingId]);

  const handleDoubleClick = useCallback((e, overlay) => {
    e.stopPropagation();
    setEditingId(overlay.id);
  }, []);

  const handleBlur = useCallback((overlay, textRef) => {
    setEditingId(null);
    if (!textRef.current) return;
    const newText = textRef.current.textContent || '';
    if (newText !== overlay.text) {
      dispatch({
        type: PROJECT_ACTIONS.UPDATE_TEXT_OVERLAY,
        payload: { id: overlay.id, updates: { text: newText } },
      });
    }
  }, [dispatch]);

  if (overlays.length === 0) return null;

  return (
    <OverlayContainer>
      {overlays.map(overlay => (
        <OverlayItem
          key={overlay.id}
          overlay={overlay}
          timeSec={timeSec}
          isEditing={editingId === overlay.id}
          onDragStart={handleDragStart}
          onDoubleClick={handleDoubleClick}
          onBlur={handleBlur}
        />
      ))}
    </OverlayContainer>
  );
};

const OverlayItem = ({ overlay, timeSec, isEditing, onDragStart, onDoubleClick, onBlur }) => {
  const textRef = useRef(null);
  const videoConfig = useVideoConfig();
  const project = useProject();
  // Use the original authored canvas width as the denominator for the cqw formula.
  // The render server overwrites project.width to the output resolution (e.g. 1920)
  // but stores the original in project._authoredWidth. Using the authoring width keeps
  // the font-size proportion correct: (80px authored on 800px) → 10% of any canvas.
  const projectWidth = project?._authoredWidth || project?.width || 800;
  const s = overlay.style || {};
  const anim = overlay.animation || {};
  const enterDur = anim.enterDuration || 0.5;
  const exitDur = anim.exitDuration || 0.4;
  const elapsed = timeSec - overlay.startTime;
  const remaining = overlay.endTime - timeSec;

  const useComp = isComponentAnimation(anim.enter);

  let animStyle = {};
  if (!useComp) {
    if (elapsed < enterDur) {
      const progress = elapsed / enterDur;
      animStyle = getEnterStyle(anim, progress);
    } else if (remaining < exitDur) {
      const progress = 1 - (remaining / exitDur);
      animStyle = getExitStyle(anim, progress);
    } else {
      animStyle = { opacity: 1 };
    }
  } else {
    // For component animations, handle exit only via CSS
    if (remaining < exitDur) {
      const progress = 1 - (remaining / exitDur);
      animStyle = getExitStyle(anim, progress);
    } else {
      animStyle = { opacity: 1 };
    }
  }

  const posStyle = {
    left: `${(overlay.x || 0.5) * 100}%`,
    top: `${(overlay.y || 0.5) * 100}%`,
    transform: `translate(-50%, -50%) ${animStyle.transform || ''}`.trim(),
    fontFamily: s.fontFamily || 'Inter, sans-serif',
    // fontSize stored in project-space px (e.g. 80 for 800px canvas).
    // Expressed as cqw so it scales with the container at any display size.
    fontSize: `${((s.fontSize || 32) / projectWidth) * 100}cqw`,
    fontWeight: s.fontWeight || 700,
    // For component animations like comp_titleCard, color/textShadow are handled
    // internally by the component — don't override them on the wrapper.
    color: useComp ? 'inherit' : (s.color || '#ffffff'),
    textShadow: useComp ? undefined : (s.textShadow || 'none'),
    background: s.background || 'transparent',
    borderRadius: s.borderRadius || 0,
    padding: s.padding || '0',
    letterSpacing: s.letterSpacing || 0,
    textTransform: s.textTransform || 'none',
    textAlign: s.textAlign || 'center',
    lineHeight: s.lineHeight || 1.3,
    opacity: animStyle.opacity !== undefined ? animStyle.opacity : 1,
    filter: animStyle.filter || undefined,
    clipPath: animStyle.clipPath || undefined,
  };

  // Get the component animation if applicable
  const AnimComp = useComp ? COMPONENT_ANIMATIONS[anim.enter] : null;

  return (
    <TextItem
      style={posStyle}
      onMouseDown={e => onDragStart(e, overlay)}
      onDoubleClick={e => onDoubleClick(e, overlay)}
    >
      {AnimComp && !isEditing ? (
        <AnimFrameProvider overlay={overlay} fps={videoConfig.fps || 30}>
          <AnimComp videoConfig={videoConfig} direction="up" titleCardConfig={overlay.titleCardConfig}>
            {overlay.text}
          </AnimComp>
        </AnimFrameProvider>
      ) : (
        <EditableText
          ref={textRef}
          contentEditable={isEditing}
          suppressContentEditableWarning
          onBlur={() => onBlur(overlay, textRef)}
          style={{ cursor: isEditing ? 'text' : 'inherit' }}
        >
          {overlay.text}
        </EditableText>
      )}
    </TextItem>
  );
};

export default TextOverlayRenderer;
